import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  CanAccess,
  Form,
  required,
  useDataProvider,
  useGetList,
  useGetMany,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import type { FieldValues } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { ListErrorState, ListRowsSkeleton } from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import type { BusinessArm, Project, SalesProject } from "../types";
import { MembershipBadge, type MembershipView } from "./SalesProjectsField";

// design-system.md section 8: a designed empty value, never a raw
// "null"/"undefined".
const EMPTY_VALUE = "—";

// Only non-archived projects are offered when adding a membership (Almikia
// Epic E3b-2, TASK-078's own rule): a membership is not a sale, so
// `compliance_pack_status` is deliberately NOT required here (the ticket's
// own ASSUMPTION).
const NON_ARCHIVED_PROJECTS_FILTER = { "archived@eq": false } as const;

/**
 * Active memberships of one person. Same query key as the projects card, so
 * the edit page can read the count without a second request.
 */
export const useActiveMembershipCount = (salesId: Identifier | undefined) => {
  const { data } = useGetList<SalesProject>(
    "sales_projects",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
      filter: { sales_id: salesId, "ended_at@is": null },
    },
    { enabled: salesId != null },
  );
  return data?.length ?? 0;
};

const useSalesProjectMemberships = (salesId: Identifier) => {
  const {
    data: memberships,
    isPending: membershipsPending,
    error,
    refetch,
  } = useGetList<SalesProject>("sales_projects", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "id", order: "ASC" },
    filter: { sales_id: salesId, "ended_at@is": null },
  });
  const projectIds = (memberships ?? []).map(
    (membership) => membership.project_id,
  );
  const { data: memberedProjects, isPending: memberedProjectsPending } =
    useGetMany<Project>(
      "projects",
      { ids: projectIds },
      { enabled: projectIds.length > 0 },
    );
  const { data: addableProjects, isPending: addableProjectsPending } =
    useGetList<Project>("projects", {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
      filter: NON_ARCHIVED_PROJECTS_FILTER,
    });

  const projectsById = new Map(
    (memberedProjects ?? []).map((project) => [project.id, project]),
  );
  const views: MembershipView[] = (memberships ?? []).map((membership) => ({
    id: membership.id,
    projectId: membership.project_id,
    projectName: projectsById.get(membership.project_id)?.name ?? EMPTY_VALUE,
    isLider: membership.is_lider_for_project,
  }));

  return {
    memberships: memberships ?? [],
    views,
    addableProjects: addableProjects ?? [],
    isPending:
      membershipsPending || memberedProjectsPending || addableProjectsPending,
    error,
    refetch,
  };
};

const MembershipRow = ({
  membership,
  view,
  onChanged,
}: {
  membership: SalesProject;
  view: MembershipView;
  onChanged: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [confirmingEnd, setConfirmingEnd] = useState(false);

  const { mutate: toggleLider, isPending: isToggling } = useMutation({
    mutationFn: () =>
      dataProvider.update("sales_projects", {
        id: membership.id,
        data: { is_lider_for_project: !membership.is_lider_for_project },
        previousData: membership,
      }),
    onSuccess: onChanged,
    onError: () =>
      notify("resources.sales.projects.update_error", { type: "error" }),
  });

  const { mutate: endMembership, isPending: isEnding } = useMutation({
    mutationFn: () =>
      dataProvider.update("sales_projects", {
        id: membership.id,
        data: { ended_at: new Date().toISOString() },
        previousData: membership,
      }),
    onSuccess: () => {
      setConfirmingEnd(false);
      onChanged();
      notify("resources.sales.projects.ended");
    },
    onError: () => {
      setConfirmingEnd(false);
      notify("resources.sales.projects.update_error", { type: "error" });
    },
  });

  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 p-3">
      <MembershipBadge membership={view} />
      <CanAccess resource="sales_projects" action="edit">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-11 md:min-h-8"
            disabled={isToggling || isEnding}
            onClick={() => toggleLider()}
          >
            {membership.is_lider_for_project
              ? translate("resources.sales.projects.clear_lider")
              : translate("resources.sales.projects.mark_lider")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11 md:min-h-8"
            disabled={isToggling || isEnding}
            onClick={() => setConfirmingEnd(true)}
          >
            {translate("resources.sales.projects.end")}
          </Button>
        </div>
      </CanAccess>
      <Confirm
        isOpen={confirmingEnd}
        loading={isEnding}
        confirm="resources.sales.projects.end"
        title={translate("resources.sales.projects.end_title")}
        content={translate("resources.sales.projects.end_description", {
          name: view.projectName,
        })}
        onConfirm={() => endMembership()}
        onClose={() => setConfirmingEnd(false)}
      />
    </div>
  );
};

interface AddMembershipFormValues extends FieldValues {
  project_id: Identifier;
  is_lider_for_project?: boolean;
}

const AddMembershipForm = ({
  salesId,
  choices,
  onAdded,
}: {
  salesId: Identifier;
  choices: Project[];
  onAdded: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate, isPending } = useMutation({
    mutationFn: (values: AddMembershipFormValues) =>
      dataProvider.create("sales_projects", {
        data: {
          sales_id: salesId,
          project_id: values.project_id,
          is_lider_for_project: values.is_lider_for_project ?? false,
          ended_at: null,
        },
      }),
    onSuccess: () => {
      onAdded();
      notify("resources.sales.projects.added");
    },
    onError: () =>
      notify("resources.sales.projects.add_error", { type: "error" }),
  });

  return (
    <Form
      // Resets the picker once its choices shrink after a successful add.
      key={choices.map((choice) => choice.id).join(",")}
      onSubmit={(values: FieldValues) =>
        mutate(values as AddMembershipFormValues)
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <AutocompleteInput
          source="project_id"
          label="resources.sales.projects.add_project"
          choices={choices}
          translateChoice={false}
          validate={required()}
          helperText={false}
        />
        <BooleanInput
          source="is_lider_for_project"
          label="resources.sales.projects.lider_bpo"
          helperText={false}
        />
        <Button type="submit" variant="outline" disabled={isPending}>
          {translate("resources.sales.projects.add")}
        </Button>
      </div>
    </Form>
  );
};

/**
 * "Proyectos" section on the user edit page (Almikia Epic E3b-2, TASK-086):
 * this page is only ever reached by direccion (full access) or a
 * lead_manager (her own brazo scope, `canAccess.ts`'s `canAccessSales`) --
 * every other role never gets here at all. Within it, adding, ending or
 * relabelling a membership is gated to direccion by `<CanAccess resource=
 * "sales_projects">` (see `canAccess.ts`), so a lead_manager sees this
 * read-only.
 */
export const SalesProjectsInput = ({
  salesId,
  businessArm,
  showCorretajeBlockedMessage = false,
}: {
  salesId: Identifier;
  /**
   * The effective unit (picked in the form, else stored). Corretaje people
   * have no projects (DB rule on new memberships), so the add form is hidden.
   */
  businessArm?: BusinessArm | null;
  /** True only while switching to Corretaje with active projects. */
  showCorretajeBlockedMessage?: boolean;
}) => {
  const translate = useTranslate();
  const isCorretaje = businessArm === "corretaje";
  const { memberships, views, addableProjects, isPending, error, refetch } =
    useSalesProjectMemberships(salesId);

  const activeProjectIds = new Set(memberships.map((m) => m.project_id));
  const choices = addableProjects.filter(
    (project) => !activeProjectIds.has(project.id),
  );

  return (
    <>
      <Separator />
      <Card>
        <CardHeader>
          <CardTitle>{translate("resources.sales.projects.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {isPending ? (
            <ListRowsSkeleton rows={2} columns={1} />
          ) : error ? (
            <ListErrorState
              message={translate("crm.common.list_error")}
              onRetry={() => refetch()}
            />
          ) : (
            <>
              {memberships.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {translate("resources.sales.projects.empty")}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {memberships.map((membership) => {
                    const view = views.find((v) => v.id === membership.id);
                    if (!view) return null;
                    return (
                      <MembershipRow
                        key={membership.id}
                        membership={membership}
                        view={view}
                        onChanged={refetch}
                      />
                    );
                  })}
                </div>
              )}
              {showCorretajeBlockedMessage ? (
                <p role="status" className="text-sm text-muted-foreground">
                  {translate(
                    "resources.sales.fields.corretaje_projects_blocked",
                  )}
                </p>
              ) : null}
              {isCorretaje ? null : (
                <CanAccess resource="sales_projects" action="create">
                  <AddMembershipForm
                    salesId={salesId}
                    choices={choices}
                    onAdded={refetch}
                  />
                </CanAccess>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};
